<template>
  <div class="qr-scanner-overlay" v-if="isActive">
    <div class="fullscreen-scanner">
      <qrcode-stream
        ref="qrcodeStream"
        :camera="cameraMode"
        :torch="torchEnabled"
        :track="trackFrame"
        :formats="supportedFormats"
        @decode="onDecode"
        @init="onInit"
      ></qrcode-stream>
    </div>

    <div class="outer-mask">
      <div class="mask-top"></div>
      <div class="mask-bottom"></div>
      <div class="mask-left"></div>
      <div class="mask-right"></div>
    </div>

    <div class="header-center">扫一扫</div>
    <div class="close-button" @click="closeScanner">×</div>

    <div class="scanner-container">
      <div class="scanner-box">
        <div class="overlay-ui">
          <div class="scan-line"></div>
          <div class="corner top-left"></div>
          <div class="corner top-right"></div>
          <div class="corner bottom-left"></div>
          <div class="corner bottom-right"></div>
        </div>
      </div>

      <div
        class="flashlight-display"
        :class="{
          'flashlight-display--active': torchEnabled,
          'flashlight-display--disabled': !torchSupported,
        }"
        @click="toggleTorch"
      >
        <svg class="flashlight-icon" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" width="30" height="30">
          <path
            d="M747.52 138.24l4.4288 1.2032c5.91872 1.65888 9.76384 2.95424 11.5456 3.8912C804.44416 164.8128 819.2 188.61568 819.2 214.66112v136.4736c0 5.64224-0.57344 11.14112-1.664 16.4352-1.8432 15.6416-9.2928 30.04416-20.93568 40.07936l-106.94656 92.1856-0.74752 0.42496-23.30624 20.1216v283.83744c0 42.20416-32.09216 76.42112-71.68 76.42112H424.96c-39.58784 0-71.68-34.21696-71.68-76.42112l-0.00512-282.53696L248.94464 431.616a23.99232 23.99232 0 0 1-3.88608-4.2752l-22.81472-19.6864c-12.08832-10.43456-19.18464-25.0112-20.9408-40.26368A81.53088 81.53088 0 0 1 199.68 351.1296V214.66112C199.68 172.45696 231.77216 138.24 271.36 138.24h476.16z m-7.2192 61.44H271.36c-8.18176 0-14.86848 6.84544-15.3344 15.47264L256 216.1152v136.98048c0 1.23904 0.11776 2.41664 0.34816 3.54304 0.3584 1.792 0.65024 3.6096 0.86016 5.4272 0.03072 0.3072 0.10752 0.512 0.27136 0.71168l0.19456 0.19456 22.81472 19.75808c1.77152 1.536 3.456 3.17952 5.03808 4.93056l1.2544 1.44384 101.9392 88.34048c12.7488 11.04896 20.33664 27.47392 20.84864 44.95872l0.0256 1.87904 0.00512 283.5968c0 8.7552 6.39488 15.90784 14.45888 16.4096L424.96 824.32h168.96c8.18176 0 14.86848-6.84544 15.3344-15.47264l0.0256-0.96768v-284.89728c0-17.5616 7.15264-34.21184 19.5328-45.63968l1.34656-1.2032 23.30624-20.1984c1.11616-0.96768 2.26816-1.88928 3.45088-2.7648L761.1904 362.9568a1.39776 1.39776 0 0 0 0.4608-0.89088c0.21504-1.80224 0.50176-3.59424 0.8704-5.376 0.1536-0.75776 0.256-1.54624 0.31232-2.3552l0.04096-1.23392V216.4736l-0.37888-0.52224c-2.8672-3.88096-8.97024-8.94976-18.9952-14.67904l-2.42688-1.3568-0.77824-0.23552zM509.44 481.28a33.28 33.28 0 0 1 33.28 33.28v92.16a33.28 33.28 0 1 1-66.56 0v-92.16a33.28 33.28 0 0 1 33.28-33.28z"
            fill="currentColor"
          />
        </svg>
        <span class="flashlight-text">
          {{ torchSupported ? (torchEnabled ? '关闭补光灯' : '打开补光灯') : '当前设备不支持补光灯' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { QrcodeStream } from 'vue-qrcode-reader';
import { Toast } from 'vant';

const SCAN_INTERVAL = 1000;
const SUPPORTED_FORMATS = ['qr_code'];

export default {
  name: 'QrScanner',
  components: {
    QrcodeStream,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loseOutTime: {
      type: Number,
      default: 5,
    },
    qrCodeValidDuration: {
      type: Number,
      default: 20,
    },
  },
  data() {
    return {
      isActive: this.visible,
      cameraMode: 'auto',
      torchEnabled: false,
      torchSupported: false,
      scanCounter: 0,
      lastScanTime: 0,
      scanTimer: null,
      promptTimer: null,
      supportedFormats: SUPPORTED_FORMATS,
    };
  },
  watch: {
    visible(newVal) {
      this.isActive = newVal;

      if (newVal) {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          Toast.fail('当前浏览器不支持摄像头访问，请更换浏览器或升级系统');
          this.$emit('input', false);
          return;
        }

        this.resetScannerState();
      } else {
        this.cleanup();
      }
    },
  },
  methods: {
    resetScannerState() {
      this.lastScanTime = 0;
      this.scanCounter = 0;
      this.torchEnabled = false;
      this.torchSupported = false;
    },

    trackFrame() {
      // Providing a track callback makes vue-qrcode-reader scan more frequently.
    },

    onDecode(decodedString) {
      const now = Date.now();

      if (now - this.lastScanTime < SCAN_INTERVAL) {
        return;
      }

      let isExpiring = false;

      try {
        const parts = decodedString.split('_');
        if (parts.length >= 3) {
          const timestamp = parseInt(parts[2], 10);
          if (!Number.isNaN(timestamp)) {
            const elapsed = Math.floor((now - timestamp) / 1000);
            const remainingTime = this.qrCodeValidDuration - elapsed;

            if (remainingTime < this.loseOutTime) {
              isExpiring = true;
            }

            if (remainingTime <= 0) {
              this.$emit('scan-expired');
              this.cleanup();
              return;
            }
          }
        }
      } catch (error) {
        console.warn('解析二维码时间戳失败:', error);
      }

      this.lastScanTime = now;
      this.scanCounter += 1;

      this.$emit('scan-success', decodedString, this.scanCounter, isExpiring);
      this.cleanup();
    },

    closeScanner() {
      this.cleanup();
      this.$emit('input', false);
      this.$emit('scan-closed');
    },

    async onInit(promise) {
      try {
        const result = await promise;
        const capabilities = (result && result.capabilities) || {};
        this.torchSupported = Boolean(capabilities.torch);
      } catch (error) {
        this.handleCameraError(error);
      }
    },

    toggleTorch() {
      if (!this.torchSupported) {
        Toast('当前设备不支持补光灯');
        return;
      }

      this.torchEnabled = !this.torchEnabled;
    },

    handleCameraError(error) {
      const errorName = error && error.name ? error.name : 'UnknownError';
      const errorMessages = {
        NotAllowedError: '请允许摄像头访问权限',
        NotFoundError: '未找到可用摄像头',
        NotSupportedError: '当前浏览器不支持摄像头访问，请更换浏览器或升级系统',
        StreamApiNotSupportedError: '当前浏览器不支持摄像头访问，请更换浏览器或升级系统',
        OverconstrainedError: '当前设备摄像头参数不兼容，请尝试更换设备或浏览器',
        NotReadableError: '摄像头可能被占用，请尝试重启应用或设备',
      };

      const message = errorMessages[errorName] || '扫码初始化失败，请重试';
      Toast.fail(message);
      this.$emit('input', false);
    },

    cleanup() {
      if (this.scanTimer) {
        clearTimeout(this.scanTimer);
        this.scanTimer = null;
      }

      if (this.promptTimer) {
        clearTimeout(this.promptTimer);
        this.promptTimer = null;
      }
    },

    handleKeydown(e) {
      if (e.key === 'Escape' && this.isActive) {
        this.closeScanner();
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    this.cleanup();
    document.removeEventListener('keydown', this.handleKeydown);
  },
};
</script>

<style scoped>
.qr-scanner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  overflow: hidden;
}

.fullscreen-scanner {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.fullscreen-scanner >>> .qrcode-reader {
  width: 100%;
  height: 100%;
}

.fullscreen-scanner >>> .qrcode-reader__camera {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.fullscreen-scanner >>> .qrcode-reader__pause-icon,
.fullscreen-scanner >>> .qrcode-reader__tracking-canvas {
  display: none;
}

.outer-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.mask-top {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 20vh;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.mask-bottom {
  position: absolute;
  top: calc(20vh + 300px);
  left: 0;
  width: 100%;
  height: calc(100vh - 20vh - 300px);
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.mask-left {
  position: absolute;
  top: 20vh;
  left: 0;
  width: calc(50% - 150px);
  height: 300px;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.mask-right {
  position: absolute;
  top: 20vh;
  right: 0;
  width: calc(50% - 150px);
  height: 300px;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}

.close-button {
  position: absolute;
  top: 40px;
  right: 30px;
  color: white;
  font-size: 40px;
  line-height: 1;
  cursor: pointer;
  font-weight: 300;
  z-index: 1002;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.header-center {
  position: absolute;
  top: 50px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 17px;
  font-weight: bold;
  text-align: center;
  z-index: 1002;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}

.scanner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
  z-index: 2;
}

.scanner-box {
  position: relative;
  width: 300px;
  height: 300px;
  margin-top: 20vh;
  border-radius: 8px;
  overflow: hidden;
  z-index: 3;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}

.overlay-ui {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.corner {
  position: absolute;
  width: 25px;
  height: 25px;
  border-style: solid;
  border-color: #fff;
  z-index: 3;
}

.corner.top-left {
  top: -2px;
  left: -2px;
  border-width: 4px 0 0 4px;
}

.corner.top-right {
  top: -2px;
  right: -2px;
  border-width: 4px 4px 0 0;
}

.corner.bottom-left {
  bottom: -2px;
  left: -2px;
  border-width: 0 0 4px 4px;
}

.corner.bottom-right {
  bottom: -2px;
  right: -2px;
  border-width: 0 4px 4px 0;
}

.scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(to right, transparent, #ffc107, transparent);
  box-shadow: 0 0 10px #ffc107;
  animation: scan-animation 2.5s linear infinite;
  z-index: 3;
}

@keyframes scan-animation {
  0% { top: 0; }
  50% { top: calc(100% - 2px); }
  100% { top: 0; }
}

.flashlight-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 30px;
  color: #fff;
  z-index: 1002;
  pointer-events: auto;
  cursor: pointer;
  user-select: none;
  transition: transform 0.15s ease, opacity 0.15s ease;
}

.flashlight-display:active {
  transform: scale(0.98);
}

.flashlight-display--disabled {
  opacity: 0.65;
}

.flashlight-display--active .flashlight-icon,
.flashlight-display--active .flashlight-text {
  color: #ffc107;
}

.flashlight-icon {
  color: #fff;
  margin-bottom: 8px;
  filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.5));
}

.flashlight-text {
  font-size: 14px;
  text-shadow: 0 0 5px rgba(0, 0, 0, 0.5);
}
</style>
